<template>
  <v-card 
    v-if="isVisible" 
    :style="sectionStyle" 
    class="mb-6 section-card" 
    elevation="0"
  >
    <v-card-title class="pb-2">
      <h3 class="text-h6 font-weight-semibold d-flex align-center mb-0 section-title">
        <v-icon class="mr-2 section-icon">mdi-flag</v-icon>
        Competitor Activity
      </h3>
    </v-card-title>
    
    <div class="px-4 pb-2">
      <p class="subtitle text-body mb-0 section-subtitle">
        Recent contract wins from tracked competitors
      </p>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="text-center py-8">
      <v-progress-circular indeterminate color="#ff1463"></v-progress-circular>
    </div>

    <!-- Empty State -->
    <div v-else-if="!activities || activities.length === 0" class="text-center py-8 grey--text">
      No competitor activity found
    </div>

    <!-- Activity List -->
    <v-card-text v-else class="pa-0">
      <div 
        v-for="(activity, index) in paginatedActivities" 
        :key="`activity-${activity.tender_id}-${index}`"
        class="activity-card pa-6"
        :class="{ 'activity-card-border-top': index > 0 }"
        @click="navigateToTender(activity.tender_id)"
      >
        <div class="d-flex justify-space-between align-start">
          <div class="flex-grow-1">
            <!-- Cluster chips (one per matched cluster) + category -->
            <div class="d-flex align-center flex-wrap gap-2 mb-2">
              <span
                v-for="(cluster, cIdx) in (activity.matched_clusters || [])"
                :key="clusterChipKey(activity, cluster, cIdx)"
                class="d-inline-flex"
              >
                <router-link
                  v-if="cluster.cluster_id != null && cluster.cluster_id !== ''"
                  :to="`/cluster/${cluster.cluster_id}`"
                  class="text-decoration-none"
                  target="_blank"
                  @click.stop
                >
                  <v-chip x-small class="cluster-chip-link">
                    {{ cluster.cluster_name }}
                    <v-icon x-small class="ml-1">mdi-open-in-new</v-icon>
                  </v-chip>
                </router-link>
                <v-chip
                  v-else
                  x-small
                  class="cluster-chip"
                >
                  {{ cluster.cluster_name }}
                </v-chip>
              </span>
              <v-chip
                v-if="activity.category"
                x-small
                class="activity-category-chip"
              >
                <v-icon
                  x-small
                  left
                  class="activity-category-chip-icon"
                >
                  mdi-tag-outline
                </v-icon>
                {{ activity.category }}
              </v-chip>
            </div>
            
            <!-- Supplier Info -->
            <div class="text-body-2 mb-2">
              <span class="font-weight-semibold">
                <router-link 
                  v-if="activity.supplier_id" 
                  :to="`/supplier/${activity.supplier_id}`" 
                  class="text-decoration-none supplier-link"
                  target="_blank"
                >
                  {{ activity.supplier_name }}
                </router-link>
                <span v-else>{{ activity.supplier_name }}</span>
              </span>
              <span class="grey--text"> won a contract</span>
            </div>

            <!-- Tender Title -->
            <router-link 
              :to="`/tender/${activity.tender_id}`" 
              class="tender-title-link d-flex align-center mb-3"
              target="_blank"
              @click.stop
            >
              {{ activity.title }}
              <v-icon small class="ml-2 grey--text">mdi-open-in-new</v-icon>
            </router-link>

            <!-- Activity Metadata -->
            <div class="d-flex align-center gap-4 text-body-2 grey--text mb-3 activity-metadata">
              <span class="d-flex align-center">
                <v-icon small class="mr-1">mdi-map-marker</v-icon>
                <router-link 
                  v-if="activity.publisher_id" 
                  :to="`/entity/${activity.publisher_id}`" 
                  class="text-decoration-none publisher-link"
                  target="_blank"
                >
                  {{ activity.publisher_name }}
                </router-link>
                <span v-else>{{ activity.publisher_name }}</span>
              </span>
              <span class="d-flex align-center">
                <v-icon small class="mr-1">mdi-cash</v-icon>
                {{ formatCurrency(activity.value) }}
              </span>
              <span class="d-flex align-center">
                <v-icon small class="mr-1">mdi-calendar</v-icon>
                {{ formatDate(activity.contract_start) }}
              </span>
            </div>
          </div>

          <!-- Analyze Button -->
          <v-btn
            small
            depressed
            class="ml-4 analyze-btn"
            @click.stop="handleAnalyze(activity)"
          >
            Analyze
          </v-btn>
        </div>
      </div>

      <!-- Pagination -->
      <v-card-actions 
        v-if="showPagination" 
        class="pagination-container pa-4"
      >
        <div class="d-flex justify-space-between align-center" style="width: 100%;">
          <div class="text-body pagination-text">
            Showing {{ paginationStart }} - {{ paginationEnd }} of {{ activities.length }}
          </div>
          <div class="d-flex gap-2">
            <v-btn
              icon
              x-small
              :disabled="currentPage === 1"
              @click="goToPreviousPage"
              class="pagination-button"
            >
              <v-icon small>mdi-chevron-left</v-icon>
            </v-btn>
            <v-btn
              icon
              x-small
              :disabled="currentPage >= totalPages"
              @click="goToNextPage"
              class="pagination-button"
            >
              <v-icon small>mdi-chevron-right</v-icon>
            </v-btn>
          </div>
        </div>
      </v-card-actions>
    </v-card-text>
  </v-card>
</template>

<script>
export default {
  name: 'CompetitorActivitySection',
  
  props: {
    activities: {
      type: Array,
      default: () => []
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    sectionStyle: {
      type: String,
      default: ''
    },
    isVisible: {
      type: Boolean,
      default: true
    },
    itemsPerPage: {
      type: Number,
      default: 10
    }
  },

  data() {
    return {
      currentPage: 1
    };
  },

  computed: {
    paginatedActivities() {
      if (!this.activities || this.activities.length === 0) return [];
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.activities.slice(start, end);
    },

    totalPages() {
      if (!this.activities || this.activities.length === 0) return 0;
      return Math.ceil(this.activities.length / this.itemsPerPage);
    },

    showPagination() {
      return this.activities && this.activities.length > this.itemsPerPage;
    },

    paginationStart() {
      return (this.currentPage - 1) * this.itemsPerPage + 1;
    },

    paginationEnd() {
      return Math.min(this.currentPage * this.itemsPerPage, this.activities.length);
    }
  },

  watch: {
    activities() {
      // Reset to first page when activities change
      this.currentPage = 1;
    }
  },

  methods: {
    navigateToTender(tenderId) {
      this.$router.push(`/tender/${tenderId}`);
    },

    handleAnalyze(activity) {
      // Emit event for analyze action
      this.$emit('analyze', activity);
      // Or navigate directly if needed
      // this.$router.push(`/tender/${activity.tender_id}`);
    },

    goToPreviousPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },

    goToNextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    },

    formatCurrency(value) {
      if (!value) return "$0";
      if (value >= 1000000) {
        return `$${(value / 1000000).toFixed(1)}M`;
      } else if (value >= 1000) {
        return `$${(value / 1000).toFixed(0)}K`;
      }
      return `$${value.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
    },

    formatDate(dateStr) {
      if (!dateStr) return "";
      try {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      } catch (e) {
        return String(dateStr);
      }
    },

    clusterChipKey(activity, cluster, cIdx) {
      const id = cluster.cluster_id != null ? cluster.cluster_id : 'x';
      const name = cluster.cluster_name || '';
      return `cluster-${activity.tender_id}-${cIdx}-${id}-${name}`;
    }
  }
};
</script>

<style scoped>
.section-title {
  color: #1f2937;
}

.section-icon {
  color: #6b7280;
}

.section-subtitle {
  color: #6b7280;
}

.activity-card {
  cursor: pointer;
  border-bottom: 1px solid #e5e7eb;
  background-color: #fef2f2;
  border-left: 3px solid #f87171;
  transition: background-color 0.2s ease;
}

.activity-card:hover {
  background-color: #fee2e2;
}

.activity-card-border-top {
  border-top: 1px solid #e5e7eb;
}

.tender-title-link {
  color: #1f2937;
  font-size: 1.25rem;
  font-weight: 500;
  text-decoration: none;
}

.tender-title-link:hover {
  text-decoration: underline;
}

.activity-metadata {
  flex-wrap: wrap;
}

.analyze-btn {
  background-color: #ffebf3;
  color: #ff1463;
  text-transform: none;
  font-weight: 500;
  font-size: 13px;
}

.analyze-btn:hover {
  background-color: #fce7f3;
}

.cluster-chip,
.cluster-chip-link {
  background-color: #e6f0f5;
  color: #12253a;
}

.cluster-chip-link {
  cursor: pointer;
}

/* Category chip — aligned with SavedSearchMetaChips */
.activity-category-chip.v-chip {
  background-color: #112233 !important;
  color: #ffffff !important;
}

.activity-category-chip .v-chip__content {
  color: #ffffff !important;
}

.activity-category-chip-icon {
  color: #ffffff !important;
}

.supplier-link,
.publisher-link {
  color: inherit;
}

.supplier-link:hover,
.publisher-link:hover {
  text-decoration: underline;
}

.pagination-container {
  border-top: 1px solid #e5e7eb;
  background-color: #f9fafb;
}

.pagination-text {
  color: #6b7280;
}

.pagination-button {
  color: #6b7280;
}
</style>